import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaDownload, 
  FaUser, 
  FaCalendarAlt, 
  FaFileAlt, 
  FaCreditCard,
  FaMoneyBillWave,
  FaCheckCircle,
  FaInfoCircle
} from "react-icons/fa";
import { api_srv } from "services";
import { useQuery } from "react-query";

// Enhanced Status Badge with proper colors
const StatusBadge = ({ state }) => {
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'settled':
        return {
          className: 'badge badge-success',
          icon: FaCheckCircle,
          color: 'text-success'
        };
      case 'not settled':
        return {
          className: 'badge badge-warning',
          icon: FaInfoCircle,
          color: 'text-warning'
        };
      default:
        return {
          className: 'badge badge-secondary',
          icon: FaInfoCircle,
          color: 'text-secondary'
        };
    }
  };

  const config = getStatusConfig(state);
  const IconComponent = config.icon;

  return (
    <span className={`${config.className} d-inline-flex align-items-center`}>
      <IconComponent className="me-1" size={12} />
      {state}
    </span>
  );
};

// Enhanced Info Item with better icon alignment
const InfoItem = ({ icon: Icon, label, value, iconColor = "text-primary" }) => (
  <div className="d-flex align-items-start mb-3">
    <div className="flex-shrink-0 me-3 mt-1">
      <div className={`rounded-circle bg-light p-2 d-flex align-items-center justify-content-center ${iconColor}`} style={{ width: '40px', height: '40px' }}>
        <Icon size={18} />
      </div>
    </div>
    <div className="flex-grow-1">
      <small className="text-muted d-block mb-1 font-weight-medium">{label}</small>
      <span className="text-dark font-weight-bold">{value}</span>
    </div>
  </div>
);

// Page Header with Breadcrumb
const PageHeader = ({ commission, onBack, onDownload }) => (
  <div className="bg-white border-bottom mb-4">
    <div className="container py-4">
      {/* Header Content */}
      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h4 className="font-weight-bold mb-1 text-capitalize">Commission Details</h4>
          <small className="text-muted">Policy #{commission.policy}</small>
        </div>
        <button
          className="btn btn-primary d-flex align-items-center px-4"
          onClick={onDownload}
        >
          <FaDownload className="me-2" size={16} />
          Download
        </button>
      </div>
    </div>
  </div>
);

// Main Commission Overview Card
const CommissionOverview = ({ commission }) => (
  <div className="card mg-b-20 border-0 shadow-sm">
    <div className="card-body p-4">
      {/* Commission Header */}
      <div className="d-flex align-items-center justify-content-between mb-4 pb-3 border-bottom">
        <div className="d-flex align-items-center">
          <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3 d-flex align-items-center justify-content-center" style={{ width: '60px', height: '60px' }}>
            <FaFileAlt className="text-primary" size={24} />
          </div>
          <div>
            <h5 className="font-weight-bold mb-2">Policy #{commission.policy}</h5>
            <StatusBadge state={commission.state} />
          </div>
        </div>
        <div className="text-right">
          <small className="text-muted d-block mb-1">Commission Amount</small>
          <h3 className="text-primary font-weight-bold mb-0 d-flex align-items-center">
            <FaMoneyBillWave className="me-2" size={20} />
            KES {commission.amount.toLocaleString()}
          </h3>
        </div>
      </div>

      {/* Commission Details */}
      <div className="row">
        {/* <div className="col-md-6">
          <InfoItem 
            icon={FaUser} 
            label="Client Name" 
            value={commission.client}
            iconColor="text-info"
          />
        </div> */}
        {/* <div className="col-md-6">
          <InfoItem 
            icon={FaUser} 
            label="Insurance Agent" 
            value={commission.agent}
            iconColor="text-success"
          />
        </div> */}
        <div className="col-md-6">
          <InfoItem 
            icon={FaCalendarAlt} 
            label="Date Earned" 
            value={commission.date}
            iconColor="text-warning"
          />
        </div>
        {/* <div className="col-md-6">
          <InfoItem 
            icon={FaCreditCard} 
            label="Payment Method" 
            value="Bank Transfer"
            iconColor="text-primary"
          />
        </div> */}
      </div>
    </div>
  </div>
);

// Payment Breakdown Section
const PaymentBreakdown = ({ commission }) => (
  <div className="row g-4">
    <div className="col-md-6">
      <div className="card mg-b-20 border-0 shadow-sm h-100">
        <div className="card-body p-4">
          <h6 className="font-weight-bold mb-4 d-flex align-items-center">
            <div className="bg-opacity-10 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: '35px', height: '35px' }}>
              <FaMoneyBillWave className="text-success" size={16} />
            </div> Commission Breakdown
          </h6>
          
          <div className="space-y-3">
            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
              <span className="text-muted">Base Commission</span>
              <span className="font-weight-bold">KES {commission.amount.toLocaleString()}</span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
              <span className="text-muted">Processing Fee</span>
              <span className="font-weight-bold">KES 0</span>
            </div>
            <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
              <span className="text-muted">Tax Deduction</span>
              <span className="font-weight-bold">KES 0</span>
            </div>
            <div className="bg-light rounded p-3 mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="font-weight-bold text-dark">Total Amount</span>
                <span className="font-weight-bold text-primary h5 mb-0">KES {commission.amount.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="col-md-6">
      <div className="card mg-b-20 border-0 shadow-sm h-100">
        <div className="card-body p-4">
          <h6 className="font-weight-bold mb-4 d-flex align-items-center">
            <div className=" bg-opacity-10 rounded-circle p-2 me-3 d-flex align-items-center justify-content-center" style={{ width: '35px', height: '35px' }}>
              <FaCreditCard className="text-info" size={16} />
            </div>
            Payment Information
          </h6>

          <div className="space-y-3">
            <InfoItem 
              icon={FaCalendarAlt} 
              label="Payment Date" 
              value={commission.date}
              iconColor="text-warning"
            />
            {/* <InfoItem 
              icon={FaCreditCard} 
              label="Payment Method" 
              value="Direct Bank Transfer"
              iconColor="text-info"
            /> */}
            <div className="mt-4">
              <div className="bg-light rounded p-3">
                <small className="text-muted d-block mb-1">Commission Reference</small>
                <code className="text-dark font-weight-medium">{commission.id}-{Date.now().toString().slice(-6)}</code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

function CommissionDetail() {
  const history = useHistory();
  const { id } = useParams();

  // Modified get_commission_details to accept id
  const get_commission_details = async (id) => {
    console.log('Step 2: Fetching commission with ID:', id);
    let resp = await (await api_srv).fetchCommissionDetails(id);
    console.log("commissions details==", resp.data);
    if (resp && resp.data) {
      const data = resp.data;
      return {
        policy: data.quotationSystemRef || 'N/A',
        amount: data.amount || 0,
        client: data.clientDetails?.name || 'Unknown Client',
        agent: data.intermediarySystemRef || 'Unknown Agent',
        date: data.createdAt ? new Date(data.createdAt).toLocaleDateString() : 'N/A',
        state: data.commisionSettlement?.commsionSettlementStatus || 'Pending',
        id: data.systemEntityCommisionRef || id
      };
    }
    throw new Error('Invalid response structure');
  };

  // Use useQuery with get_commission_details
  const { data: commission, isLoading, isError } = useQuery(
    ['commissions_details', id], // Unique query key including the id
    () => get_commission_details(id),
    {
      onError: (error) => {
        console.error('Step 3: Error in get_commission_details:', error);
        console.log('Step 3: Error details:', JSON.stringify(error, null, 2));
      },
      retry: 1, // Limit retry attempts
      staleTime: 5 * 60 * 1000, // 5 minutes stale time
    }
  );

  const handleDownload = () => {
    if (commission) {
      const receiptData = {
        policy: commission.policy,
        amount: commission.amount,
        client: commission.client,
        agent: commission.agent,
        date: commission.date,
        downloadDate: new Date().toLocaleDateString()
      };
      
      console.log('Downloading receipt:', receiptData);
      alert(`Receipt downloaded successfully for Policy #${commission.policy}`);
    }
  };

  const handleBack = () => {
    history.push("/dashboard/account-management/commissions");
  };

  if (isLoading) return <LoadingState />;
  if (isError || !commission) return <NotFoundState onBack={handleBack} />;

  return (
    <div>
      <PageHeader 
        commission={commission} 
        onBack={handleBack}
        onDownload={handleDownload}
      />
      
      <div className="container py-4">
        <CommissionOverview commission={commission} />
        <PaymentBreakdown commission={commission} />
      </div>
    </div>
  );
}

// Enhanced Loading State
const LoadingState = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
    <div className="text-center">
      <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="sr-only">Loading...</span>
      </div>
      <h6 className="text-muted mb-2">Loading commission details...</h6>
      <small className="text-muted">Please wait while we fetch your information</small>
    </div>
  </div>
);

// Enhanced Not Found State
const NotFoundState = ({ onBack }) => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '500px' }}>
    <div className="text-center">
      <div className="bg-light rounded-circle mx-auto mb-4 d-flex align-items-center justify-content-center" style={{ width: "100px", height: "100px" }}>
        <FaFileAlt className="text-muted" size={40} />
      </div>
      <h4 className="font-weight-bold mb-3">Commission Not Found</h4>
      <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '400px' }}>
        We couldn't find the commission record you're looking for. It may have been moved or deleted.
      </p>
      <button 
        className="btn btn-primary px-4 d-flex align-items-center mx-auto" 
        onClick={onBack}
      >
        <FaArrowLeft className="me-2" />
        Back to Commissions
      </button>
    </div>
  </div>
);

export default CommissionDetail;